import React, { useState } from 'react';
import './Workbook.css';

const schoolWeeks = [
  "Aug 4-10", "Aug 11-17", "Aug 18-24", "Aug 25-31",
  "Sep 1-7", "Sep 8-14", "Sep 15-21", "Sep 22-28",
  "Sep 29-Oct 5", "Oct 6-12", "Oct 13-19", "Oct 20-26",
  "Oct 27-Nov 2", "Nov 3-9", "Nov 10-16", "Nov 17-23",
  "Nov 24-30", "Dec 1-7",
  "Jan 5-11", "Jan 12-18", "Jan 19-25", "Jan 26-Feb 1",
  "Feb 2-8", "Feb 9-15", "Feb 16-22", "Feb 23-Mar 1",
  "Mar 2-8", "Mar 9-15", "Mar 16-22", "Mar 23-29",
  "Mar 30-Apr 5", "Apr 6-12", "Apr 13-19", "Apr 20-26",
  "Apr 27-May 3", "May 4-10"
];

const fallWeeks = schoolWeeks.slice(0, 12);
const winterWeeks = schoolWeeks.slice(12, 24);
const springWeeks = schoolWeeks.slice(24, 36);

const summerWeeks = [
  "Jun 1-7", "Jun 8-14", "Jun 15-21", "Jun 22-28",
  "Jun 29-Jul 5", "Jul 6-12", "Jul 13-19", "Jul 20-26"
];

function WeekCard({ semester, weekNumber, isSummer = false, isCollapsed, setIsCollapsed, parentRole }) {

  const weekId = `week-${semester.toLowerCase()}-${weekNumber}`;

  const getStorageKey = (key) => `workbook_${parentRole}_${weekId}_${key}`;

  const getInitialState = (key, defaultVal) => {
    const stored = localStorage.getItem(getStorageKey(key));
    if (stored) return JSON.parse(stored);
    return defaultVal;
  };

  const [waterLevels, setWaterLevels] = React.useState(() => getInitialState('water', Array(7).fill(0)));
  const [adultLevels, setAdultLevels] = React.useState(() => getInitialState('adult', Array(7).fill(0)));
  const [eyerollLevels, setEyerollLevels] = React.useState(() => getInitialState('eyeroll', Array(7).fill(0)));

  const defaultDayData = Array(7).fill({
    parentSleep: '',
    childSleep: '',
    exercise: '',
    parentEnergy: '',
    childMood: '',
    hardMoment: '',
    hardToday: '',
    hardTodaySelfTalk: '',
    didWellToday: '',
    groundRegulate: '',
    tomorrowIntention: ''
  });

  const [dayData, setDayData] = React.useState(() => getInitialState('dayData', defaultDayData));

  React.useEffect(() => {
    localStorage.setItem(getStorageKey('water'), JSON.stringify(waterLevels));
  }, [waterLevels, parentRole, weekId]);

  React.useEffect(() => {
    localStorage.setItem(getStorageKey('adult'), JSON.stringify(adultLevels));
  }, [adultLevels, parentRole, weekId]);

  React.useEffect(() => {
    localStorage.setItem(getStorageKey('eyeroll'), JSON.stringify(eyerollLevels));
  }, [eyerollLevels, parentRole, weekId]);

  React.useEffect(() => {
    localStorage.setItem(getStorageKey('dayData'), JSON.stringify(dayData));
  }, [dayData, parentRole, weekId]);

  const handleDayDataChange = (dayIndex, field, value) => {
    setDayData(prev => {
      const newData = [...prev];
      newData[dayIndex] = { ...newData[dayIndex], [field]: value };
      return newData;
    });
  };

  const isDayComplete = (dayIndex) => {
    const data = dayData[dayIndex];
    return data.parentSleep !== '' &&
           data.childSleep !== '' &&
           data.exercise !== '' &&
           data.parentEnergy !== '' &&
           data.childMood !== '' &&
           data.hardMoment.trim() !== '' &&
           data.hardToday.trim() !== '' &&
           data.hardTodaySelfTalk.trim() !== '' &&
           data.didWellToday.trim() !== '' &&
           data.groundRegulate.trim() !== '' &&
           data.tomorrowIntention.trim() !== '' &&
           waterLevels[dayIndex] > 0 &&
           adultLevels[dayIndex] > 0 &&
           eyerollLevels[dayIndex] > 0;
  };

  const setLevel = (setter, dayIndex, val) => {
    setter(prev => {
      const newArr = [...prev];
      newArr[dayIndex] = val;
      return newArr;
    });
  };

  const renderEmojis = (type, currentLevel, maxLevel, dayIndex, emoji) => {
    let setter;
    if (type === 'water') setter = setWaterLevels;
    if (type === 'adult') setter = setAdultLevels;
    if (type === 'eyeroll') setter = setEyerollLevels;

    return (
      <div className="flex gap-2 text-4xl cursor-pointer">
        {Array.from({ length: maxLevel }).map((_, i) => (
          <span
            key={i}
            onClick={() => setLevel(setter, dayIndex, i + 1)}
            style={{
              opacity: i < currentLevel ? 1 : 0.2,
              filter: i < currentLevel ? 'none' : 'grayscale(100%)',
              transition: 'all 0.2s'
            }}
          >
            {emoji}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="week-card" id={weekId}>
      <div
        className="week-title flex justify-between items-center cursor-pointer select-none"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span className="text-2xl font-bold">{semester} Week {weekNumber}</span>
        <span className="text-4xl leading-none print:hidden" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{isCollapsed ? '+' : '-'}</span>
      </div>


        <div className={isCollapsed ? "hidden print:block mt-6" : "block mt-6"}>
          <table>
            <thead>
              <tr>
                <th className="bg-white z-10 p-2 shadow-sm uppercase text-xs text-gray-500 font-bold border-b-2 border-[#ece5df]">Day</th>
                <th className="bg-white z-10 p-2 shadow-sm uppercase text-xs text-gray-500 font-bold border-b-2 border-[#ece5df]">Parent Sleep</th>
                <th className="bg-white z-10 p-2 shadow-sm uppercase text-xs text-gray-500 font-bold border-b-2 border-[#ece5df]">Child Sleep</th>
                <th className="bg-white z-10 p-2 shadow-sm uppercase text-xs text-gray-500 font-bold border-b-2 border-[#ece5df]">Exercise</th>

                <th className="bg-white z-10 p-2 shadow-sm uppercase text-xs text-gray-500 font-bold border-b-2 border-[#ece5df]">Parent energy</th>
                <th className="bg-white z-10 p-2 shadow-sm uppercase text-xs text-gray-500 font-bold border-b-2 border-[#ece5df]">Child mood</th>
                <th className="bg-white z-10 p-2 shadow-sm uppercase text-xs text-gray-500 font-bold border-b-2 border-[#ece5df]">Hard moment / win</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5, 6, 7].map((d, index) => (
                <React.Fragment key={d}>
                  <tr style={{ background: isDayComplete(index) ? '#dcfce7' : (index % 2 !== 0 ? '#fdf8f5' : 'white') }}>
                    <td style={{ background: isDayComplete(index) ? '#bbf7d0' : '#ece5df', textAlign: 'center', fontWeight: 'bold', verticalAlign: 'middle', height: '100px', fontSize: '40px', color: 'white' }}>
                      {d} {isDayComplete(index) && <span style={{ fontSize: '1rem', color: 'initial' }}><br/>✅</span>}
                    </td>
                    <td>
                      <select value={dayData[index].parentSleep} onChange={(e) => handleDayDataChange(index, 'parentSleep', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="None">None</option>
                        <option value="-5">-5</option>
                        <option value="5-6">5-6</option>
                        <option value="7-8">7-8</option>
                        <option value="9+">9+</option>
                      </select>
                    </td>
                    <td>
                      <select value={dayData[index].childSleep} onChange={(e) => handleDayDataChange(index, 'childSleep', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="None">None</option>
                        <option value="-5">-5</option>
                        <option value="5-6">5-6</option>
                        <option value="7-8">7-8</option>
                        <option value="9+">9+</option>
                      </select>
                    </td>
                    <td>
                      <select value={dayData[index].exercise} onChange={(e) => handleDayDataChange(index, 'exercise', e.target.value)}>
                        <option value="">Select...</option>
                        <option value="None">None</option>
                        <option value="5 min">5 min</option>
                        <option value="10 min">10 min</option>
                        <option value="20+ min">20+ min</option>
                      </select>
                    </td>

                    <td><input type="number" min="1" max="10" style={{ width: '70px' }} value={dayData[index].parentEnergy} onChange={(e) => handleDayDataChange(index, 'parentEnergy', e.target.value)} /></td>
                    <td><input type="number" min="1" max="10" style={{ width: '70px' }} value={dayData[index].childMood} onChange={(e) => handleDayDataChange(index, 'childMood', e.target.value)} /></td>
                    <td><input type="text" placeholder="brief note" value={dayData[index].hardMoment} onChange={(e) => handleDayDataChange(index, 'hardMoment', e.target.value)} /></td>
                  </tr>
                  <tr className="beverage-row">
                    <td colSpan="8" style={{ padding: '12px', background: isDayComplete(index) ? '#dcfce7' : (index % 2 !== 0 ? '#fdf8f5' : '#fdfcfb'), borderBottom: '2px solid #ece5df' }}>
                      <div className="flex flex-col xl:flex-row justify-between gap-6">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-500 uppercase">Water:</span>
                          {renderEmojis('water', waterLevels[index], 8, index, '💧')}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-500 uppercase">Adult Beverages:</span>
                          {renderEmojis('adult', adultLevels[index], 5, index, '🍷')}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-500 uppercase">Eyerolls Ignored:</span>
                          {renderEmojis('eyeroll', eyerollLevels[index], 5, index, '🙄')}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="8" style={{ padding: '12px', background: isDayComplete(index) ? '#dcfce7' : (index % 2 !== 0 ? '#fdf8f5' : '#fdfcfb'), borderBottom: '4px solid #ece5df' }}>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-gray-500 uppercase">What was hard today?</label>
                        <input type="text" className="w-full text-sm" value={dayData[index].hardToday} onChange={(e) => handleDayDataChange(index, 'hardToday', e.target.value)} />

                        <label className="text-xs font-bold text-gray-500 uppercase mt-2">How can you tell yourself about the hard day?</label>
                        <input type="text" className="w-full text-sm" value={dayData[index].hardTodaySelfTalk} onChange={(e) => handleDayDataChange(index, 'hardTodaySelfTalk', e.target.value)} />

                        <label className="text-xs font-bold text-gray-500 uppercase mt-2">What did you do well today?</label>
                        <input type="text" className="w-full text-sm" value={dayData[index].didWellToday} onChange={(e) => handleDayDataChange(index, 'didWellToday', e.target.value)} />

                        <label className="text-xs font-bold text-gray-500 uppercase mt-2">What did you do to ground or regulate yourself today?</label>
                        <input type="text" className="w-full text-sm" value={dayData[index].groundRegulate} onChange={(e) => handleDayDataChange(index, 'groundRegulate', e.target.value)} />

                        <label className="text-xs font-bold text-gray-500 uppercase mt-2">Your intention for tomorrow?</label>
                        <input type="text" className="w-full text-sm" value={dayData[index].tomorrowIntention} onChange={(e) => handleDayDataChange(index, 'tomorrowIntention', e.target.value)} />
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: '30px', background: '#fff5eb', padding: '30px', borderRadius: '24px', border: '2px solid #ffb347', boxShadow: '4px 4px 0px 0px rgba(255,179,71,0.3)' }}>
            <div className="text-2xl" style={{ background: '#ffb347', color: '#fff', padding: '12px 24px', borderRadius: '16px', display: 'inline-block', marginBottom: '20px', fontWeight: 'bold' }}>
              📝 Weekly Taunt Tracker
            </div>
            <div className="flex flex-col gap-2">
              <label>📅 Week of (dates):</label> <input type="text" placeholder="e.g., Aug 4-10" />
              <label>😤 Top taunt/trigger this week:</label> <textarea rows="2" placeholder="What pushed your buttons?"></textarea>
              <label>💭 My reaction / how I handled it:</label> <textarea rows="2"></textarea>
              <label>🔄 Reframe / what I learned:</label> <textarea rows="2"></textarea>
              {isSummer && (
                <>
                  <label>🍦 Summer-specific struggle (heat, camps, boredom):</label> <textarea rows="2"></textarea>
                </>
              )}
              <label>🏆 One small win this week:</label> <input type="text" />
            </div>
          </div>
        </div>
    </div>
  );
}


export default function Workbook() {
  const [parentRole, setParentRole] = useState(() => localStorage.getItem('parentRole') || '');
  const [selectedWeek, setSelectedWeek] = useState(() => localStorage.getItem('selectedWeek') || null);

  React.useEffect(() => {
    localStorage.setItem('parentRole', parentRole);
  }, [parentRole]);

  React.useEffect(() => {
    if (selectedWeek) {
      localStorage.setItem('selectedWeek', selectedWeek);
    }
  }, [selectedWeek]);

  const getGlobalState = (key, defaultVal) => {
    const stored = localStorage.getItem(`workbook_${parentRole}_global_${key}`);
    if (stored) return JSON.parse(stored);
    return defaultVal;
  };

  const [progressionAug, setProgressionAug] = useState(() => getGlobalState('progressionAug', 0));
  const [progressionNow, setProgressionNow] = useState(() => getGlobalState('progressionNow', 0));
  const [worksheet, setWorksheet] = useState(() => getGlobalState('worksheet', {
    carpoolHours: '',
    cookHours: '',
    regulatorHours: '',
    otherRole: '',
    otherHours: '',
    otherRate: ''
  }));

  const [groundingCustom, setGroundingCustom] = useState(() => getGlobalState('groundingCustom', []));
  const [groundingInput, setGroundingInput] = useState('');

  const [affirmationsCustom, setAffirmationsCustom] = useState(() => getGlobalState('affirmationsCustom', []));
  const [affirmationsInput, setAffirmationsInput] = useState('');

  const [selfCareCustom, setSelfCareCustom] = useState(() => getGlobalState('selfCareCustom', []));
  const [selfCareInput, setSelfCareInput] = useState('');

  const [adviceCustom, setAdviceCustom] = useState(() => getGlobalState('adviceCustom', []));
  const [adviceInput, setAdviceInput] = useState('');

  // Sync state when role changes (prevents overwriting new role with old state)
  React.useEffect(() => {
    setProgressionAug(getGlobalState('progressionAug', 0));
    setProgressionNow(getGlobalState('progressionNow', 0));
    setWorksheet(getGlobalState('worksheet', {
      carpoolHours: '', cookHours: '', regulatorHours: '',
      otherRole: '', otherHours: '', otherRate: ''
    }));
    setGroundingCustom(getGlobalState('groundingCustom', []));
    setAffirmationsCustom(getGlobalState('affirmationsCustom', []));
    setSelfCareCustom(getGlobalState('selfCareCustom', []));
    setAdviceCustom(getGlobalState('adviceCustom', []));
  }, [parentRole]);

  React.useEffect(() => {
    localStorage.setItem(`workbook_${parentRole}_global_progressionAug`, JSON.stringify(progressionAug));
    localStorage.setItem(`workbook_${parentRole}_global_progressionNow`, JSON.stringify(progressionNow));
    localStorage.setItem(`workbook_${parentRole}_global_worksheet`, JSON.stringify(worksheet));
    localStorage.setItem(`workbook_${parentRole}_global_groundingCustom`, JSON.stringify(groundingCustom));
    localStorage.setItem(`workbook_${parentRole}_global_affirmationsCustom`, JSON.stringify(affirmationsCustom));
    localStorage.setItem(`workbook_${parentRole}_global_selfCareCustom`, JSON.stringify(selfCareCustom));
    localStorage.setItem(`workbook_${parentRole}_global_adviceCustom`, JSON.stringify(adviceCustom));
  }, [progressionAug, progressionNow, worksheet, groundingCustom, affirmationsCustom, selfCareCustom, adviceCustom, parentRole]);

  const handleAddCustom = (setter, stateVar, val) => {
    if (val.trim() !== '') {
      setter([...stateVar, val]);
    }
  };

  const handleWorksheetChange = (field, value) => {
    setWorksheet(prev => ({ ...prev, [field]: value }));
  };

  const getNum = (val) => {
    const n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  };

  const jumpToCurrentWeek = () => {
    const now = new Date();
    const month = now.getMonth(); // 0 = Jan, 11 = Dec
    let targetSemester = "";
    let targetWeekNum = 1;

    // Very rough approximation for finding the current week
    if (month >= 7 && month <= 11) { // Aug - Dec (Fall)
      targetSemester = "fall";
      const weeksSinceAug = Math.floor((now - new Date(now.getFullYear(), 7, 1)) / (7 * 24 * 60 * 60 * 1000));
      targetWeekNum = Math.min(Math.max(weeksSinceAug + 1, 1), fallWeeks.length);
    } else if (month >= 0 && month <= 4) { // Jan - May (Spring)
      targetSemester = "spring";
      const weeksSinceJan = Math.floor((now - new Date(now.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
      targetWeekNum = Math.min(Math.max(weeksSinceJan + 1, 1), springWeeks.length);
    } else { // Jun - Jul (Summer)
      targetSemester = "summer";
      const weeksSinceJun = Math.floor((now - new Date(now.getFullYear(), 5, 1)) / (7 * 24 * 60 * 60 * 1000));
      targetWeekNum = Math.min(Math.max(weeksSinceJun + 1, 1), summerWeeks.length);
    }

    const targetId = `week-${targetSemester}-${targetWeekNum}`;

    // First, set the active week so it mounts on the screen
    let semesterCap = targetSemester.charAt(0).toUpperCase() + targetSemester.slice(1);
    setSelectedWeek(`${semesterCap}-${targetWeekNum}`);

    // Then wait for React to render and scroll to it
    setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        scrollTo('weekly-logs');
      }
    }, 100);
  };

  const carpoolTotal = getNum(worksheet.carpoolHours) * 20;
  const cookTotal = getNum(worksheet.cookHours) * 25;
  const regulatorTotal = getNum(worksheet.regulatorHours) * 50;
  const otherTotal = getNum(worksheet.otherHours) * getNum(worksheet.otherRate);
  const grandTotal = carpoolTotal + cookTotal + regulatorTotal + otherTotal;

  React.useEffect(() => {
    if (selectedWeek) {
      setTimeout(() => {
        const el = document.getElementById('weekly-logs');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset - 150;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
  };

  return (
    <div className="workbook-page pb-20">
      <div className="workbook-container">
        <div className="workbook-header">
          <div className="logo-badge"><span>❤️</span><span>The Unraveled Parent</span></div>
          <h1>Raising Humans Who Push Every Button</h1>
          <div className="tagline">Weekly Structure – Daily Log + Weekly Taunt Tracker (Full School Year & Summer)</div>

          <p style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '16px' }}>
            <span><strong>Parent Role:</strong> <input type="text" style={{ width: '180px', marginLeft: '8px' }} placeholder="e.g. Mom, Dad, Both" value={parentRole} onChange={e => setParentRole(e.target.value)} /></span>
            <span><strong>School Year:</strong> <input type="text" style={{ width: '120px', marginLeft: '8px' }} placeholder="2026-2027" /></span>
            <button onClick={jumpToCurrentWeek} title="This Week" className="bg-brand-rose text-[#333] hover:bg-brand-red flex items-center justify-center gap-1 print:hidden" style={{ width: 'auto', padding: '0 12px', borderRadius: '20px', fontSize: '1rem' }}><span>This Week</span> 🗓️</button>
            <span className="hidden print:inline"><strong>This Week:</strong> <input type="text" style={{ width: '120px', marginLeft: '8px' }} placeholder="" /></span>
          </p>
        </div>

          <div className="quick-jump-menu print:hidden" style={{ top: "80px" }}>
            <span className="font-bold mr-3 text-brand-slate">Quick Jump:</span>
            <button onClick={() => scrollTo('weekly-logs')} title="Weekly Logs">📅</button>
            <button onClick={() => scrollTo('grounding')} title="Grounding Techniques">🧘</button>
            <button onClick={() => scrollTo('affirmations')} title="Affirmations">💬</button>
            <button onClick={() => scrollTo('self-care')} title="Self-Care">🧖</button>
            <button onClick={() => scrollTo('vent')} title="Voodoo-Doll Vent Page">🗣️</button>
            <button onClick={() => scrollTo('body-mapping')} title="Body-Mapping Pain">📍</button>
            <button onClick={() => scrollTo('terrible-advice')} title="Terrible Advice Hall of Fame">📢</button>
            <button onClick={() => scrollTo('lies')} title="Lies vs. Truths">🎭</button>
            <button onClick={() => scrollTo('priceless')} title="Priceless Worksheet">💰</button>
            <button onClick={() => scrollTo('progression')} title="Year-Long Progression">📈</button>
          </div>

        <div className="workbook-inner" id="weekly-logs">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 print:hidden mb-12 mt-12">
          {/* Fall Semester Card */}
          <div className="bg-brand-rose text-white rounded-3xl p-4 shadow-md flex flex-col items-center">
            <span className="text-4xl mb-2">🍂</span>
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-whimsical)' }}>Fall Semester</h2>
            <p className="opacity-90 italic text-xs text-center mb-4">Aug – Oct | Routines</p>
            <div className="grid grid-cols-2 gap-2 w-full">
              {fallWeeks.map((week, idx) => (
                <button
                  key={`fall-${idx}`}
                  onClick={() => setSelectedWeek(`Fall-${idx + 1}`)}
                  className={`py-1 px-2 rounded-lg text-sm font-bold ${selectedWeek === `Fall-${idx + 1}` ? 'bg-white text-brand-rose' : 'bg-brand-rose border border-white hover:bg-white hover:text-brand-rose'}`}
                >
                  Wk {idx + 1}
                </button>
              ))}
            </div>
          </div>

          {/* Winter Semester Card */}
          <div className="bg-brand-rose text-white rounded-3xl p-4 shadow-md flex flex-col items-center">
            <span className="text-4xl mb-2">❄️</span>
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-whimsical)' }}>Winter Semester</h2>
            <p className="opacity-90 italic text-xs text-center mb-4">Nov – Jan | Holidays</p>
            <div className="grid grid-cols-2 gap-2 w-full">
              {winterWeeks.map((week, idx) => (
                <button
                  key={`winter-${idx}`}
                  onClick={() => setSelectedWeek(`Winter-${idx + 1}`)}
                  className={`py-1 px-2 rounded-lg text-sm font-bold ${selectedWeek === `Winter-${idx + 1}` ? 'bg-white text-brand-rose' : 'bg-brand-rose border border-white hover:bg-white hover:text-brand-rose'}`}
                >
                  Wk {idx + 1}
                </button>
              ))}
            </div>
          </div>

          {/* Spring Semester Card */}
          <div className="bg-brand-rose text-white rounded-3xl p-4 shadow-md flex flex-col items-center">
            <span className="text-4xl mb-2">🌷</span>
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-whimsical)' }}>Spring Semester</h2>
            <p className="opacity-90 italic text-xs text-center mb-4">Feb – May | Testing</p>
            <div className="grid grid-cols-2 gap-2 w-full">
              {springWeeks.map((week, idx) => (
                <button
                  key={`spring-${idx}`}
                  onClick={() => setSelectedWeek(`Spring-${idx + 1}`)}
                  className={`py-1 px-2 rounded-lg text-sm font-bold ${selectedWeek === `Spring-${idx + 1}` ? 'bg-white text-brand-rose' : 'bg-brand-rose border border-white hover:bg-white hover:text-brand-rose'}`}
                >
                  Wk {idx + 1}
                </button>
              ))}
            </div>
          </div>

          {/* Summer Semester Card */}
          <div className="bg-brand-rose text-white rounded-3xl p-4 shadow-md flex flex-col items-center">
            <span className="text-4xl mb-2">☀️</span>
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'var(--font-whimsical)' }}>Summer Survival</h2>
            <p className="opacity-90 italic text-xs text-center mb-4">Jun – Jul | No routine</p>
            <div className="grid grid-cols-2 gap-2 w-full">
              {summerWeeks.map((week, idx) => (
                <button
                  key={`summer-${idx}`}
                  onClick={() => setSelectedWeek(`Summer-${idx + 1}`)}
                  className={`py-1 px-2 rounded-lg text-sm font-bold ${selectedWeek === `Summer-${idx + 1}` ? 'bg-white text-brand-rose' : 'bg-brand-rose border border-white hover:bg-white hover:text-brand-rose'}`}
                >
                  Wk {idx + 1}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="print:hidden">
          {selectedWeek && selectedWeek.startsWith('Fall-') && (
            <WeekCard key={`fall-${parentRole}`} parentRole={parentRole} semester="Fall" weekNumber={parseInt(selectedWeek.split('-')[1])} isCollapsed={false} setIsCollapsed={() => {}} />
          )}
          {selectedWeek && selectedWeek.startsWith('Winter-') && (
            <WeekCard key={`winter-${parentRole}`} parentRole={parentRole} semester="Winter" weekNumber={parseInt(selectedWeek.split('-')[1])} isCollapsed={false} setIsCollapsed={() => {}} />
          )}
          {selectedWeek && selectedWeek.startsWith('Spring-') && (
            <WeekCard key={`spring-${parentRole}`} parentRole={parentRole} semester="Spring" weekNumber={parseInt(selectedWeek.split('-')[1])} isCollapsed={false} setIsCollapsed={() => {}} />
          )}
          {selectedWeek && selectedWeek.startsWith('Summer-') && (
            <WeekCard key={`summer-${parentRole}`} parentRole={parentRole} semester="Summer" weekNumber={parseInt(selectedWeek.split('-')[1])} isSummer={true} isCollapsed={false} setIsCollapsed={() => {}} />
          )}
        </div>

        {/* Printable PDF Section */}
        <div className="hidden print:block">
          <div className="semester-divider">
            <h3>🍂 Fall Semester</h3>
            <p>Aug – Oct | Routines</p>
          </div>
          {fallWeeks.map((weekLabel, idx) => (
            <WeekCard parentRole={parentRole} key={`print-fall-${idx}-${parentRole}`} semester="Fall" weekNumber={idx + 1} isCollapsed={false} setIsCollapsed={() => {}} />
          ))}

          <div className="semester-divider">
            <h3>❄️ Winter Semester</h3>
            <p>Nov – Jan | Holidays</p>
          </div>
          {winterWeeks.map((weekLabel, idx) => (
            <WeekCard parentRole={parentRole} key={`print-winter-${idx}-${parentRole}`} semester="Winter" weekNumber={idx + 1} isCollapsed={false} setIsCollapsed={() => {}} />
          ))}

          <div className="semester-divider">
            <h3>🌷 Spring Semester</h3>
            <p>Feb – May | Testing</p>
          </div>
          {springWeeks.map((weekLabel, idx) => (
            <WeekCard parentRole={parentRole} key={`print-spring-${idx}-${parentRole}`} semester="Spring" weekNumber={idx + 1} isCollapsed={false} setIsCollapsed={() => {}} />
          ))}

          <div className="semester-divider">
            <h3>☀️ Summer Break</h3>
            <p>Jun – Jul | No routine</p>
          </div>
          {summerWeeks.map((weekLabel, idx) => (
            <WeekCard parentRole={parentRole} key={`print-summer-${idx}-${parentRole}`} semester="Summer" weekNumber={idx + 1} isSummer={true} isCollapsed={false} setIsCollapsed={() => {}} />
          ))}

        </div>

        {/* Shared resources */}
        <div className="workbook-inner">
          <div className="semester-divider">
            <h3>📘 Year-Long Tools</h3>
            <p>Grounding, affirmations, self-care, venting, reframes</p>
          </div>

          <h2 id="grounding">🧘 Grounding Techniques</h2>
          <div style={{ background: '#f7f3f0', padding: '20px', borderRadius: '28px' }}>
            <div className="checkbox-group flex-col !items-start">
              <label><input type="checkbox" /> <strong>1. Anchor breath:</strong> Inhale 4, exhale 6. Keep voice low.</label>
              <label><input type="checkbox" /> <strong>2. Feet-on-floor reset:</strong> Press feet down, bend knees.</label>
              <label><input type="checkbox" /> <strong>3. One-phrase grounding:</strong> “Stay calm” / “They’re having a hard time.”</label>
              <label><input type="checkbox" /> <strong>4. Micro body release:</strong> Unclench jaw, drop shoulders.</label>
              <label><input type="checkbox" /> <strong>5. Narrow focus:</strong> “Just keep my voice steady for 2 minutes.”</label>
              <label><input type="checkbox" /> <strong>6. Sensory reset:</strong> Cold water on hands.</label>
              <label><input type="checkbox" /> <strong>7. After-moment discharge:</strong> Shake hands, step away 30 sec.</label>
              {groundingCustom.map((item, idx) => (
                <label key={idx}><input type="checkbox" /> <strong>{item}</strong></label>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <input type="text" className="font-bold" value={groundingInput} onChange={e => setGroundingInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { handleAddCustom(setGroundingCustom, groundingCustom, groundingInput); setGroundingInput(''); } }} placeholder="➕ Add your own..." />
              <button onClick={() => { handleAddCustom(setGroundingCustom, groundingCustom, groundingInput); setGroundingInput(''); }} className="bg-brand-rose text-white px-4 py-2 rounded-xl text-sm font-bold print:hidden">Add</button>
            </div>
            <div className="mt-4">
              <label>📌 My go-to combo:</label> <input type="text" placeholder="e.g., anchor breath + drop shoulders" />
            </div>
          </div>

          <h2 id="affirmations">💬 Affirmations</h2>
          <div className="checkbox-group">
            <label><input type="checkbox" /> I can stay calm even when things feel out of control.</label>
            <label><input type="checkbox" /> My child's behavior is communication, not an attack.</label>
            <label><input type="checkbox" /> I don't have to solve everything right now.</label>
            <label><input type="checkbox" /> This moment will pass.</label>
            <label><input type="checkbox" /> I am doing the best I can with what I have.</label>
            <label><input type="checkbox" /> Connection matters more than control.</label>
            <label><input type="checkbox" /> Small wins count.</label>
            <label><input type="checkbox" /> I will get through this phase.</label>
            {affirmationsCustom.map((item, idx) => (
              <label key={idx}><input type="checkbox" /> {item}</label>
            ))}
          </div>
          <div className="mt-4 mb-4 flex gap-2">
            <input type="text" className="font-bold" value={affirmationsInput} onChange={e => setAffirmationsInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { handleAddCustom(setAffirmationsCustom, affirmationsCustom, affirmationsInput); setAffirmationsInput(''); } }} placeholder="➕ Add your own affirmation..." />
            <button onClick={() => { handleAddCustom(setAffirmationsCustom, affirmationsCustom, affirmationsInput); setAffirmationsInput(''); }} className="bg-brand-rose text-white px-4 py-2 rounded-xl text-sm font-bold print:hidden">Add</button>
          </div>
          <label>✨ My top 3:</label> <textarea rows="2"></textarea>

          <h2 id="self-care">🧖 Self-Care (Non-Instagram)</h2>
          <div className="checkbox-group">
            <label><input type="checkbox" /> Take 10 min before getting out of the car</label>
            <label><input type="checkbox" /> 5 slow breaths</label>
            <label><input type="checkbox" /> Step into another room</label>
            <label><input type="checkbox" /> Cold water on face</label>
            <label><input type="checkbox" /> Music to shift mood</label>
            <label><input type="checkbox" /> Drink water + eat a meal</label>
            <label><input type="checkbox" /> Get outside 5 min</label>
            <label><input type="checkbox" /> Write to unload</label>
            <label><input type="checkbox" /> Text a friend who gets it</label>
            <label><input type="checkbox" /> Trade childcare time</label>
            <label><input type="checkbox" /> Say "no" to one thing</label>
            <label><input type="checkbox" /> Go to bed 20 min earlier</label>
            {selfCareCustom.map((item, idx) => (
              <label key={idx}><input type="checkbox" /> {item}</label>
            ))}
          </div>
          <div className="mt-4 mb-4 flex gap-2">
            <input type="text" className="font-bold" value={selfCareInput} onChange={e => setSelfCareInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { handleAddCustom(setSelfCareCustom, selfCareCustom, selfCareInput); setSelfCareInput(''); } }} placeholder="➕ Add your own self-care..." />
            <button onClick={() => { handleAddCustom(setSelfCareCustom, selfCareCustom, selfCareInput); setSelfCareInput(''); }} className="bg-brand-rose text-white px-4 py-2 rounded-xl text-sm font-bold print:hidden">Add</button>
          </div>
          <label>🌱 This week I'll try:</label> <textarea rows="2"></textarea>

          <div className="flex gap-4 items-start">
            <img src="/voodoo-doll.png" alt="Voodoo Doll" className="w-32 h-32 object-cover rounded-xl mt-4 print:hidden" />
            <div className="flex-1">
              <h2 id="vent">🗣️ Voodoo-Doll Vent Page</h2>
              <textarea rows="6" style={{ background: '#fef7e8' }} placeholder="Write what you can't say out loud. Private. Then tear it out if needed."></textarea>
              <label>🎭 One string I'd pull today:</label> <input type="text" style={{ width: '100%' }} />
            </div>
          </div>

          <div className="flex flex-row-reverse gap-4 items-start mt-8">
            <img src="/pain.png" alt="Pain Map" className="w-32 h-32 object-cover rounded-xl mt-4 print:hidden" />
            <div className="flex-1">
              <h2 id="body-mapping">📍 Body-Mapping Pain</h2>
              <div style={{ background: '#fff0e0', padding: '15px', borderRadius: '20px' }}>
                <textarea rows="3" placeholder="Describe where stress lives (tight jaw, knot in stomach, heavy shoulders...)"></textarea>
                <label>What emotion in your neck/shoulders?</label> <input type="text" />
                <label>What does your stomach need to say?</label> <input type="text" />
              </div>
            </div>
          </div>

          <div className="flex gap-4 items-start mt-8">
            <img src="/advice.png" alt="Terrible Advice" className="w-32 h-32 object-cover rounded-xl mt-4 print:hidden" />
            <div className="flex-1">
              <h2 id="terrible-advice">📢 Terrible Advice Hall of Fame</h2>
          <div className="checkbox-group">
            <label><input type="checkbox" /> “Just be stricter”</label> <label><input type="checkbox" /> “It’s a phase”</label>
            <label><input type="checkbox" /> “My kid never did that”</label> <label><input type="checkbox" /> “Have you tried a reward chart?”</label>
            <label><input type="checkbox" /> “Pray about it”</label> <label><input type="checkbox" /> “Take away screens”</label>
            {adviceCustom.map((item, idx) => (
              <label key={idx}><input type="checkbox" /> {item}</label>
            ))}
          </div>
          <div className="mt-4 mb-4 flex gap-2">
            <input type="text" className="font-bold" value={adviceInput} onChange={e => setAdviceInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { handleAddCustom(setAdviceCustom, adviceCustom, adviceInput); setAdviceInput(''); } }} placeholder="➕ Add your own..." />
            <button onClick={() => { handleAddCustom(setAdviceCustom, adviceCustom, adviceInput); setAdviceInput(''); }} className="bg-brand-rose text-white px-4 py-2 rounded-xl text-sm font-bold print:hidden">Add</button>
          </div>
          <label>🔄 What I actually need to hear:</label> <textarea rows="2"></textarea>
            </div>
          </div>

          <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-start my-8">
            <div className="mb-8 lg:mb-0">
              <h2 id="lies">🎭 Lies vs. Truths</h2>
          <table>
            <thead>
              <tr><th>Lie</th><th>Truth</th></tr>
            </thead>
            <tbody>
              <tr><td><label><input type="checkbox" /> If I were better, this wouldn't happen.</label> </td><td>Challenging behavior ≠ parenting failure</td></tr>
              <tr><td><label><input type="checkbox" /> Other families don't deal with this.</label> </td><td>Many struggle silently. You're not alone.</td></tr>
              <tr><td><label><input type="checkbox" /> I should stay calm every time.</label> </td><td>Repair matters more than perfection.</td></tr>
              <tr><td><input type="text" placeholder="Your lie" /> <label><input type="checkbox" /> check</label></td><td><input type="text" placeholder="Your truth" /></td></tr>
            </tbody>
          </table>
            </div>
            <div>
              <h2 id="priceless">💰 Priceless Worksheet (Monthly)</h2>
          <table>
            <thead>
              <tr><th>Role</th><th>Hours/mo</th><th>Rate</th><th>Total</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Carpool driver</td>
                <td><input type="number" value={worksheet.carpoolHours} onChange={e => handleWorksheetChange('carpoolHours', e.target.value)} style={{ width: '60px' }} /></td>
                <td>$20</td>
                <td><span className="print-total print:block hidden">${carpoolTotal.toFixed(2)}</span><input type="text" value={`$${carpoolTotal.toFixed(2)}`} readOnly className="print:hidden w-20 bg-gray-50" /></td>
              </tr>
              <tr>
                <td>Cook/meal planner</td>
                <td><input type="number" value={worksheet.cookHours} onChange={e => handleWorksheetChange('cookHours', e.target.value)} style={{ width: '60px' }} /></td>
                <td>$25</td>
                <td><span className="print-total print:block hidden">${cookTotal.toFixed(2)}</span><input type="text" value={`$${cookTotal.toFixed(2)}`} readOnly className="print:hidden w-20 bg-gray-50" /></td>
              </tr>
              <tr>
                <td>Emotional regulator</td>
                <td><input type="number" value={worksheet.regulatorHours} onChange={e => handleWorksheetChange('regulatorHours', e.target.value)} style={{ width: '60px' }} /></td>
                <td>$50</td>
                <td><span className="print-total print:block hidden">${regulatorTotal.toFixed(2)}</span><input type="text" value={`$${regulatorTotal.toFixed(2)}`} readOnly className="print:hidden w-20 bg-gray-50" /></td>
              </tr>
              <tr>
                <td><input type="text" placeholder="Other role" value={worksheet.otherRole} onChange={e => handleWorksheetChange('otherRole', e.target.value)} style={{ minWidth: '100px' }} /></td>
                <td><input type="number" value={worksheet.otherHours} onChange={e => handleWorksheetChange('otherHours', e.target.value)} style={{ width: '60px' }} /></td>
                <td>$<input type="number" value={worksheet.otherRate} onChange={e => handleWorksheetChange('otherRate', e.target.value)} style={{ width: '60px' }} /></td>
                <td><span className="print-total print:block hidden">${otherTotal.toFixed(2)}</span><input type="text" value={`$${otherTotal.toFixed(2)}`} readOnly className="print:hidden w-20 bg-gray-50" /></td>
              </tr>
            </tbody>
          </table>
          <p><strong>Total monthly value:</strong> <span className="print-total print:inline hidden font-bold">${grandTotal.toFixed(2)}</span><input type="text" value={`$${grandTotal.toFixed(2)}`} readOnly className="print:hidden w-24 font-bold bg-gray-50" /> &nbsp; <em>actual pay: $0 / Your Worth: Priceless</em></p>
            </div>
          </div>

          <h2 id="progression">📈 Year-Long Progression</h2>
          <div className="mb-6">
            <p className="mb-2"><strong>Start of school year (August):</strong> Cope level (1-10)</p>
            <div className="progress-bar-custom">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <div
                  key={`aug-${num}`}
                  className="progress-segment cursor-pointer select-none"
                  onClick={() => setProgressionAug(num)}
                  style={{ background: num <= progressionAug ? '#ff7a7b' : '', color: num <= progressionAug ? 'white' : '' }}
                >
                  <span className="text-xl font-bold">{num}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mb-6">
            <p className="mb-2"><strong>Now (current season):</strong> Cope level (1-10)</p>
            <div className="progress-bar-custom">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <div
                  key={`now-${num}`}
                  className="progress-segment cursor-pointer select-none"
                  onClick={() => setProgressionNow(num)}
                  style={{ background: num <= progressionNow ? '#ff7a7b' : '', color: num <= progressionNow ? 'white' : '' }}
                >
                  <span className="text-xl font-bold">{num}</span>
                </div>
              ))}
            </div>
          </div>
          <label>What got better?</label> <textarea rows="2"></textarea>
          <label>One skill I'll carry forward:</label> <input type="text" />

          <div className="footer-note text-center mt-12 pt-8 border-t border-brand-sand">
            <p className="mb-4">❤️ You tracked every week. That's resilience.</p>
            <div className="mb-8">
              Support resources: <input type="text" style={{ width: '70%', marginTop: '6px' }} placeholder="Therapist, hotline, parent group" />
            </div>
            <div className="print:block hidden text-gray-500 text-sm font-bold mt-8">© 2025 The Unraveled Parent. All rights reserved. Not for commercial distribution.</div>
          </div>
        </div>
      </div>

      <button className="floating-print-btn print:hidden" onClick={() => window.print()} title="Print / Save as PDF">
        🖨️
      </button>

      </div>
    </div>
  );
}
